"use client";

import { useCallback, useState } from "react";
import { useActionClientFirestore, useActionServerFunction } from "./ActionContext";
import { useAppDrawer } from "./AppDrawerContext";
import { useClientInstallId, useClientType, useClientVersion } from "./ClientContext";
import { useFeedbackToast } from "./FeedbackContext";
import { useUserSignOut } from "./UserContext";

export default function useServerAction(actionName) {
  const toast = useFeedbackToast();
  const client = useClientType();
  const clientVersion = useClientVersion();
  const installId = useClientInstallId();
  const httpsFunction = useActionServerFunction();
  const onSignOut = useUserSignOut();
  const drawer = useAppDrawer();
  const { doc, setDoc, arrayUnion, getFirestore } = useActionClientFirestore();
  const [request, setRequest] = useState();
  const [response, setResponse] = useState();

  const onCall = useCallback(
    async (params, ...args) => {
      const onSucceed = args?.find((arg) => typeof arg === "function");

      try {
        setRequest(params || {});

        const response = await httpsFunction(
          {
            ...params,
            client,
            clientVersion,
            installId,
            ...(process.env.NODE_ENV === "development" && { isDevMode: true }),
          },
          actionName // Action name is Firebase function name
        );

        setResponse(response.data || {});

        if (typeof response.data?.success === "string") {
          toast.success(response.data.success);
        } else if (typeof response.data?.info === "string") {
          toast.info(response.data.info);
        } else {
          const successMessage = args?.find((arg) => typeof arg === "string");
          if (successMessage) {
            toast.success(successMessage);
          }
        }

        if (drawer?.guidePath) {
          setDoc(
            doc(getFirestore(), drawer.guidePath),
            {
              completes: arrayUnion(actionName),
            },
            { merge: true }
          );
        }

        if (onSucceed) {
          onSucceed(response.data || {});
        }
      } catch (error) {
        console.info(`%c[${actionName} failed]`, `color: red`, error.message, JSON.stringify(error.details));

        setResponse({
          error: {
            ...error.details,
            message: error.message,
          },
        });

        switch (error.details?.type) {
          case "info":
            toast.info(error.details);
            break;
          case "warning":
            toast.warning(error.details);
            break;
          default:
            if (error.details?.title || error.message) {
              toast.error(error.details?.title ? error.details : error.message);
            }
        }

        if (error.details?.signOut) {
          onSignOut(true);
        }
      } finally {
        setRequest(null);
      }
    },
    [client, installId, clientVersion, actionName, drawer]
  );

  const onClear = useCallback(() => setResponse(null), []);

  return {
    request,
    response,
    loading: Boolean(request),
    succeed: Boolean(response && !response.error),
    onCall,
    setResponse,
    onAdjust: (updates) => setResponse((response) => ({ ...response, ...updates })), // TODO: remove
    onClear,
  };
}
